import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useSpaceStore } from "../../stores";
import { getColorPatchTexture } from "./textures";
import { smoothedCameraRef } from "./useViewportSync";

interface Patch {
	x: number;
	y: number;
	scale: number;
	color: string;
	opacity: number;
	additive?: boolean;
}

// --- Seeded PRNG (deterministic positions across renders) ---

function mulberry32(seed: number) {
	let s = seed | 0;
	return () => {
		s = (s + 0x6d2b79f5) | 0;
		let t = Math.imul(s ^ (s >>> 15), 1 | s);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

// --- Galaxy patch palette ---

const NEBULA_COLORS: { color: string; additive: boolean }[] = [
	// Warm orange / amber
	{ color: "#c46a28", additive: true },
	{ color: "#a85030", additive: true },
	// Cool blue / cyan
	{ color: "#2a5a9a", additive: true },
	{ color: "#3a7aba", additive: true },
	// Purple / magenta
	{ color: "#6a2a8a", additive: true },
	{ color: "#8a3a7a", additive: true },
	// Pink / rose
	{ color: "#8a3050", additive: true },
	{ color: "#9a3858", additive: true },
	// Green tint
	{ color: "#1a6c4a", additive: true },
	{ color: "#2a5c3a", additive: true },
	// Dark dust lanes
	{ color: "#0a0810", additive: false },
	{ color: "#08060e", additive: false },
	{ color: "#060812", additive: false },
];

function generateGalaxyPatches(): Patch[] {
	const rng = mulberry32(42);
	const patches: Patch[] = [];
	const SPREAD = 3000;
	const COUNT = 70;

	for (let i = 0; i < COUNT; i++) {
		const idx = Math.floor(rng() * NEBULA_COLORS.length);
		const palette = NEBULA_COLORS[idx] ?? { color: "#0a0810", additive: false };
		const dist = 200 + rng() * (SPREAD - 200); // 200–3000 from center
		const angle = rng() * Math.PI * 2;
		patches.push({
			x: Math.cos(angle) * dist,
			y: Math.sin(angle) * dist,
			scale: 400 + rng() * 800,
			color: palette.color,
			opacity: palette.additive ? 0.04 + rng() * 0.08 : 0.07 + rng() * 0.1,
			additive: palette.additive,
		});
	}

	// A few large atmospheric haze patches
	patches.push(
		{ x: 0, y: 0, scale: 2400, color: "#12101e", opacity: 0.06 },
		{ x: 800, y: -600, scale: 1800, color: "#1e1428", opacity: 0.04 },
		{ x: -900, y: 700, scale: 2000, color: "#101828", opacity: 0.05 },
	);

	return patches;
}

const GALAXY_PATCHES = generateGalaxyPatches();

const SYSTEM_PATCHES: Patch[] = [
	{ x: 280, y: 60, scale: 800, color: "#c46a28", opacity: 0.18, additive: true },
	{ x: -200, y: 80, scale: 900, color: "#2a5a9a", opacity: 0.15, additive: true },
	{ x: 180, y: 250, scale: 700, color: "#6a2a8a", opacity: 0.14, additive: true },
	{ x: -250, y: 200, scale: 550, color: "#8a3050", opacity: 0.11, additive: true },
	{ x: -100, y: -50, scale: 700, color: "#1a6c4a", opacity: 0.08, additive: true },
	{ x: 120, y: 180, scale: 500, color: "#0a0810", opacity: 0.22 },
	{ x: 0, y: 0, scale: 1600, color: "#12101e", opacity: 0.08 },
];

/** Parallax factor — matches the deeper parallax star layers */
const PARALLAX_FACTOR = 0.05;

// --- Instanced patch shaders ---

const PATCH_VERTEX = /* glsl */ `
	attribute vec3 aColor;
	attribute float aOpacity;

	varying vec2 vUv;
	varying vec3 vColor;
	varying float vOpacity;

	void main() {
		vUv = uv;
		vColor = aColor;
		vOpacity = aOpacity;
		gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
	}
`;

const PATCH_FRAGMENT = /* glsl */ `
	uniform sampler2D uMap;

	varying vec2 vUv;
	varying vec3 vColor;
	varying float vOpacity;

	void main() {
		vec4 tex = texture2D(uMap, vUv);
		gl_FragColor = vec4(vColor * tex.rgb, tex.a * vOpacity);
	}
`;

// --- Helpers ---

const _matrix = new THREE.Matrix4();
const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _color = new THREE.Color();

function buildInstancedMesh(
	patches: Patch[],
	material: THREE.ShaderMaterial,
): THREE.InstancedMesh {
	const count = patches.length;
	const geo = new THREE.PlaneGeometry(1, 1);

	const colorArr = new Float32Array(count * 3);
	const opacityArr = new Float32Array(count);

	for (let i = 0; i < count; i++) {
		const p = patches[i] as Patch;
		_position.set(p.x, -p.y, -2);
		_scale.set(p.scale, p.scale, 1);
		_matrix.compose(_position, _quaternion, _scale);

		_color.setStyle(p.color);
		colorArr[i * 3] = _color.r;
		colorArr[i * 3 + 1] = _color.g;
		colorArr[i * 3 + 2] = _color.b;
		opacityArr[i] = p.opacity;
	}

	geo.setAttribute("aColor", new THREE.InstancedBufferAttribute(colorArr, 3));
	geo.setAttribute("aOpacity", new THREE.InstancedBufferAttribute(opacityArr, 1));

	const mesh = new THREE.InstancedMesh(geo, material, count);
	for (let i = 0; i < count; i++) {
		const p = patches[i] as Patch;
		_position.set(p.x, -p.y, -2);
		_scale.set(p.scale, p.scale, 1);
		_matrix.compose(_position, _quaternion, _scale);
		mesh.setMatrixAt(i, _matrix);
	}
	mesh.instanceMatrix.needsUpdate = true;
	mesh.frustumCulled = false;
	mesh.renderOrder = -3;

	return mesh;
}

export function AmbientColorPatches(): React.JSX.Element {
	const viewMode = useSpaceStore((s) => s.viewMode);
	const tex = useMemo(() => getColorPatchTexture(), []);
	const patches = viewMode === "system" ? SYSTEM_PATCHES : GALAXY_PATCHES;
	const groupRef = useRef<THREE.Group>(null);

	const additiveMaterial = useMemo(
		() =>
			new THREE.ShaderMaterial({
				uniforms: { uMap: { value: tex } },
				vertexShader: PATCH_VERTEX,
				fragmentShader: PATCH_FRAGMENT,
				transparent: true,
				depthWrite: false,
				blending: THREE.AdditiveBlending,
			}),
		[tex],
	);

	const normalMaterial = useMemo(
		() =>
			new THREE.ShaderMaterial({
				uniforms: { uMap: { value: tex } },
				vertexShader: PATCH_VERTEX,
				fragmentShader: PATCH_FRAGMENT,
				transparent: true,
				depthWrite: false,
				blending: THREE.NormalBlending,
			}),
		[tex],
	);

	const { additiveMesh, normalMesh } = useMemo(() => {
		const additivePatches: Patch[] = [];
		const normalPatches: Patch[] = [];
		for (const p of patches) {
			if (p.additive) {
				additivePatches.push(p);
			} else {
				normalPatches.push(p);
			}
		}
		return {
			additiveMesh: additivePatches.length > 0
				? buildInstancedMesh(additivePatches, additiveMaterial)
				: null,
			normalMesh: normalPatches.length > 0
				? buildInstancedMesh(normalPatches, normalMaterial)
				: null,
		};
	}, [patches, additiveMaterial, normalMaterial]);

	// Dispose geometry on cleanup
	useEffect(() => {
		return () => {
			additiveMesh?.geometry.dispose();
			normalMesh?.geometry.dispose();
		};
	}, [additiveMesh, normalMesh]);

	useFrame(() => {
		if (!groupRef.current) return;
		if (viewMode === "system") {
			const cam = smoothedCameraRef.current;
			groupRef.current.position.x = cam.x * PARALLAX_FACTOR;
			groupRef.current.position.y = cam.y * PARALLAX_FACTOR;
		} else {
			groupRef.current.position.x = 0;
			groupRef.current.position.y = 0;
		}
	});

	return (
		<group ref={groupRef}>
			{additiveMesh && <primitive object={additiveMesh} />}
			{normalMesh && <primitive object={normalMesh} />}
		</group>
	);
}
